/**
 * <vpc-dropdown>: select-only combobox for the "Variant picker custom" block.
 * https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
 *
 * Focus stays on the combobox; the active option is exposed with
 * aria-activedescendant. Choosing an option checks the matching hidden radio
 * and dispatches `change`, which <variant-selects> and product-info.js already
 * handle. product-info.js then replaces the whole picker with fresh server
 * markup, so the new instance restores focus from `window.__vpcFocusKey`.
 */
if (!customElements.get('vpc-dropdown')) {
  customElements.define(
    'vpc-dropdown',
    class VpcDropdown extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[role="combobox"]');
        this.list = this.querySelector('[role="listbox"]');
        this.options = Array.from(this.list.querySelectorAll('[role="option"]'));
        this.activeIndex = -1;
        this.typeahead = '';
        this.typeaheadTimer = null;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
        this.onListPointerDown = (event) => event.preventDefault(); // keep focus on the combobox
        this.onListClick = this.onListClick.bind(this);
        this.onListPointerMove = this.onListPointerMove.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);

        this.trigger.addEventListener('click', this.onTriggerClick);
        this.trigger.addEventListener('keydown', this.onTriggerKeydown);
        this.list.addEventListener('pointerdown', this.onListPointerDown);
        this.list.addEventListener('click', this.onListClick);
        this.list.addEventListener('pointermove', this.onListPointerMove);
        this.addEventListener('focusout', this.onFocusOut);

        if (window.__vpcFocusKey && window.__vpcFocusKey === this.dataset.key) {
          window.__vpcFocusKey = null;
          this.trigger.focus({ preventScroll: true });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        clearTimeout(this.typeaheadTimer);
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      get selectedIndex() {
        return this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      }

      open(index = this.selectedIndex) {
        if (this.isOpen) return;
        this.list.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('vpc__dropdown--open');
        this.placeList();
        this.setActive(index < 0 ? 0 : index);
        document.addEventListener('pointerdown', this.onDocumentPointerDown);
      }

      close() {
        if (!this.isOpen) return;
        this.list.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.removeAttribute('aria-activedescendant');
        this.classList.remove('vpc__dropdown--open', 'vpc__dropdown--up');
        this.options.forEach((option) => option.classList.remove('vpc__option--active'));
        this.activeIndex = -1;
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      }

      // Open upwards when there is not enough room below the trigger.
      placeList() {
        this.classList.remove('vpc__dropdown--up');
        const rect = this.trigger.getBoundingClientRect();
        const listHeight = Math.min(this.list.scrollHeight, 360);
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < listHeight + 16 && rect.top > spaceBelow) this.classList.add('vpc__dropdown--up');
      }

      setActive(index) {
        const last = this.options.length - 1;
        index = Math.max(0, Math.min(index, last));
        this.options.forEach((option, i) => option.classList.toggle('vpc__option--active', i === index));
        this.activeIndex = index;
        const option = this.options[index];
        if (!option) return;
        this.trigger.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }

      select(index) {
        const option = this.options[index];
        this.close();
        if (!option || option.getAttribute('aria-selected') === 'true') return;

        const input = document.getElementById(option.dataset.inputId);
        if (!input) return;

        this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
        // Show the choice right away; the server re-render follows.
        const value = this.trigger.querySelector('.vpc__value');
        const content = option.querySelector('.vpc__content');
        if (value && content) value.innerHTML = content.innerHTML;

        window.__vpcFocusKey = this.dataset.key;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onTriggerClick() {
        this.isOpen ? this.close() : this.open();
      }

      onTriggerKeydown(event) {
        const { key, altKey } = event;

        if (!this.isOpen) {
          if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) {
            event.preventDefault();
            this.open();
          } else if (key === 'Home' || key === 'End') {
            event.preventDefault();
            this.open(key === 'Home' ? 0 : this.options.length - 1);
          } else if (this.isTypeaheadKey(event)) {
            this.open();
            this.onTypeahead(key);
          }
          return;
        }

        switch (key) {
          case 'ArrowDown':
            event.preventDefault();
            this.setActive(this.activeIndex + 1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (altKey) this.select(this.activeIndex);
            else this.setActive(this.activeIndex - 1);
            break;
          case 'Home':
            event.preventDefault();
            this.setActive(0);
            break;
          case 'End':
            event.preventDefault();
            this.setActive(this.options.length - 1);
            break;
          case 'PageDown':
            event.preventDefault();
            this.setActive(this.activeIndex + 10);
            break;
          case 'PageUp':
            event.preventDefault();
            this.setActive(this.activeIndex - 10);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.select(this.activeIndex);
            break;
          case 'Escape':
            event.preventDefault();
            event.stopPropagation();
            this.close();
            // Dawn's modals (quick add) close on the Escape *keyup*: swallow it so
            // Escape only closes the list, not the whole modal.
            this.addEventListener('keyup', (keyupEvent) => keyupEvent.key === 'Escape' && keyupEvent.stopPropagation(), {
              once: true,
            });
            break;
          case 'Tab':
            this.close();
            break;
          default:
            if (this.isTypeaheadKey(event)) this.onTypeahead(key);
        }
      }

      isTypeaheadKey({ key, ctrlKey, metaKey, altKey }) {
        return key.length === 1 && key !== ' ' && !ctrlKey && !metaKey && !altKey;
      }

      // Jump to the next option whose title starts with the typed characters.
      onTypeahead(key) {
        clearTimeout(this.typeaheadTimer);
        this.typeahead += key.toLowerCase();
        this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);

        const titles = this.options.map((option) =>
          (option.querySelector('.vpc__title')?.textContent || '').trim().toLowerCase()
        );
        const start = this.typeahead.length === 1 ? this.activeIndex + 1 : this.activeIndex;
        for (let step = 0; step < titles.length; step++) {
          const index = (start + step) % titles.length;
          if (titles[index].startsWith(this.typeahead)) {
            this.setActive(index);
            return;
          }
        }
      }

      onListClick(event) {
        const option = event.target.closest('[role="option"]');
        if (option) this.select(this.options.indexOf(option));
      }

      onListPointerMove(event) {
        const option = event.target.closest('[role="option"]');
        const index = this.options.indexOf(option);
        if (index > -1 && index !== this.activeIndex) {
          this.options.forEach((item, i) => item.classList.toggle('vpc__option--active', i === index));
          this.activeIndex = index;
          this.trigger.setAttribute('aria-activedescendant', option.id);
        }
      }

      onDocumentPointerDown(event) {
        if (!this.contains(event.target)) this.close();
      }

      onFocusOut(event) {
        if (!this.contains(event.relatedTarget)) this.close();
      }
    }
  );
}
